import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  Typography,
  Button,
  CircularProgress,
  Box,
  Alert,
  TextField,
  MenuItem,
  Grid,
  Paper,
  Tabs,
  Tab,
  Switch,
  FormControlLabel,
  InputAdornment,
  FormHelperText,
  Divider,
  IconButton,
  Autocomplete,
} from "@mui/material";
import { Save, Refresh, Settings, BrandingWatermark, Notifications } from "@mui/icons-material";
import { format } from "date-fns";
import { useDispatch } from "react-redux";
import { updateScreenTitle } from "../../../../LayoutContainers/AppLayout/redux/AppSlice";
import AppService from "../../../../Configurations/Appservice";
import { showNotification } from "../../../../utils/notifications";
import { CUSTOMER_ORG_TYPE } from "../../../../Configurations/AppConfig";

/**
 * Customer Organization Configuration component
 * Allows admin to manage configuration settings for customer organizations
 */
const CustomerOrganizationConfig = () => {
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrganization, setSelectedOrganization] = useState(null);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [configLoading, setConfigLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  
  const dispatch = useDispatch();
  
  // Load organizations on component mount
  useEffect(() => {
    dispatch(updateScreenTitle("Customer Organization Settings"));
    fetchCustomerOrganizations();
    
    return () => dispatch(updateScreenTitle(""));
  }, []);
  
  /**
   * Fetch all customer organizations
   */
  const fetchCustomerOrganizations = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Fetch customer organizations
      const params = { type: CUSTOMER_ORG_TYPE };
      const orgData = await AppService.get("v1/organisation", { params });
      
      if (Array.isArray(orgData) && orgData.length > 0) {
        setOrganizations(orgData);
      } else {
        setError("No customer organizations found.");
      }
    } catch (error) {
      console.error("Error fetching customer organizations:", error);
      setError("Failed to load customer organizations.");
    } finally {
      setLoading(false);
    }
  };
  
  /**
   * Handle organization change and fetch its configuration
   */
  const handleOrganizationChange = async (event, newValue) => {
    if (!newValue) {
      setSelectedOrganization(null);
      setConfig(null);
      return;
    }
    
    setSelectedOrganization(newValue);
    fetchOrganizationConfig(newValue.id);
  };
  
  /**
   * Fetch configuration for a specific organization
   */
  const fetchOrganizationConfig = async (organizationId) => {
    setConfigLoading(true);
    setError(null);
    
    try {
      // Fetch config for this organization
      const config = await AppService.get(`api/organization-config/organization/${organizationId}`);
      // Normalize allowSelfAssign to reflect either field from backend
      const normalized = {
        ...config,
        allowSelfAssign: (config?.allowSelfAssign === true) || (config?.allow_workflow_self_assign === true)
      };
      setConfig(normalized);
    } catch (error) {
      // If no config exists, create a default one
      console.error("Error fetching organization config:", error);
      
      // Create a default config object
      const defaultConfig = {
        organizationId: organizationId,
        allowSelfAssign: false,
        allowTicketSelfAssign: false,
        allowOrgUserAssignment: false,
        selfAssignLimit: 0
      };
      
      setConfig(defaultConfig);
      setError("Failed to load organization configuration. Using default settings.");
    } finally {
      setConfigLoading(false);
    }
  };
  
  /**
   * Save the organization configuration
   */
  const handleSaveConfig = async () => {
    if (!selectedOrganization || !config) return;
    
    setSaving(true);
    setError(null);
    
    try {
      // Create the request payload
      const payload = {
        organizationId: selectedOrganization.id,
        allowSelfAssign: config.allowSelfAssign || false,
        allowTicketSelfAssign: config.allowTicketSelfAssign || false,
        allowOrgUserAssignment: config.allowOrgUserAssignment || false,
        selfAssignLimit: config.selfAssignLimit || 0
      };
      
      // Update the config using the PUT endpoint
      const updatedConfig = await AppService.put(
        `api/organization-config/organization/${selectedOrganization.id}`,
        payload
      );
      
      setConfig(updatedConfig);
      showNotification("Configuration saved successfully", "success");
    } catch (error) {
      console.error("Error saving organization config:", error);
      setError("Failed to save configuration.");
      showNotification("Failed to save configuration", "error");
    } finally {
      setSaving(false);
    }
  };
  
  /**
   * Handle switch toggle for boolean settings
   */
  const handleSwitchChange = (setting, value) => {
    setConfig(prev => ({
      ...prev,
      [setting]: value
    }));
  };
  
  /**
   * Handle change for numeric fields
   */
  const handleNumberChange = (setting, value) => {
    setConfig(prev => ({
      ...prev,
      [setting]: parseInt(value) || 0
    }));
  };
  
  /**
   * Handle tab change
   */
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };
  
  /**
   * Render the selected tab content
   */
  const renderTabContent = () => {
    switch (activeTab) {
      case 0: // GENERAL tab
        return (
          <Box sx={{ p: 3 }}>
            <Typography variant="subtitle1" gutterBottom>
              General Configuration
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Basic organization information and settings. This section is under development.
            </Typography>
            {/* Placeholder for future general configuration options */}
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Organization Name"
                  fullWidth
                  disabled
                  value={selectedOrganization?.name || ""}
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Organization ID"
                  fullWidth
                  disabled
                  value={selectedOrganization?.id || ""}
                  margin="normal"
                />
              </Grid>
            </Grid>
          </Box>
        );
      
      case 1: // SELF-ASSIGNMENT tab
        return (
          <Box sx={{ p: 3 }}>
            <Typography variant="subtitle1" gutterBottom>
              Self-Assignment Settings
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Configure how users can self-assign tickets and workflow requests.
            </Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Paper elevation={0} variant="outlined" sx={{ p: 2, mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Assignment Permissions
                  </Typography>
                  
                  <FormControlLabel
                    control={
                      <Switch
                        checked={config?.allowSelfAssign || false}
                        onChange={(e) => handleSwitchChange('allowSelfAssign', e.target.checked)}
                        color="primary"
                        disabled={configLoading}
                      />
                    }
                    label="Allow Self Assignment"
                    sx={{ mb: 2, display: 'block' }}
                  />
                  <FormHelperText sx={{ mt: -1, mb: 2 }}>
                    When enabled, users can assign workflow requests to themselves
                  </FormHelperText>
                  
                  <FormControlLabel
                    control={
                      <Switch
                        checked={config?.allowTicketSelfAssign || false}
                        onChange={(e) => handleSwitchChange('allowTicketSelfAssign', e.target.checked)}
                        color="primary"
                        disabled={configLoading}
                      />
                    }
                    label="Allow Ticket Self Assignment"
                    sx={{ mb: 2, display: 'block' }}
                  />
                  <FormHelperText sx={{ mt: -1, mb: 2 }}>
                    When enabled, users can assign tickets to themselves
                  </FormHelperText>
                  
                  <FormControlLabel
                    control={
                      <Switch
                        checked={config?.allowOrgUserAssignment || false}
                        onChange={(e) => handleSwitchChange('allowOrgUserAssignment', e.target.checked)}
                        color="primary"
                        disabled={configLoading}
                      />
                    }
                    label="Allow Organization User Assignment"
                    sx={{ mb: 2, display: 'block' }}
                  />
                  <FormHelperText sx={{ mt: -1, mb: 2 }}>
                    When enabled, managers can assign work to users within their organization
                  </FormHelperText>
                </Paper>
              </Grid>
              
              <Grid item xs={12}>
                <Paper elevation={0} variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Assignment Limits
                  </Typography>
                  
                  <TextField
                    label="Self Assignment Limit"
                    type="number"
                    fullWidth
                    value={config?.selfAssignLimit || 0}
                    onChange={(e) => handleNumberChange('selfAssignLimit', e.target.value)}
                    InputProps={{
                      inputProps: { min: 0 },
                      endAdornment: <InputAdornment position="end">items</InputAdornment>,
                    }}
                    helperText="Maximum number of tickets a user can self-assign (0 = unlimited)"
                    sx={{ mb: 2 }}
                    disabled={configLoading}
                  />
                </Paper>
              </Grid>
            </Grid>
          </Box>
        );
      
      case 2: // BRANDING tab
        return (
          <Box sx={{ p: 3 }}>
            <Typography variant="subtitle1" gutterBottom>
              Branding Settings
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Customize the appearance of the application for this organization. This section is under development.
            </Typography>
            {/* Placeholder for future branding options */}
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Organization Display Name"
                  fullWidth
                  disabled
                  value={selectedOrganization?.name || ""}
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Logo URL"
                  fullWidth
                  disabled
                  value=""
                  margin="normal"
                />
              </Grid>
            </Grid>
          </Box>
        );
      
      case 3: // NOTIFICATIONS tab
        return (
          <Box sx={{ p: 3 }}>
            <Typography variant="subtitle1" gutterBottom>
              Notification Settings
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Configure notification preferences for this organization. This section is under development.
            </Typography>
            {/* Placeholder for future notification options */}
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={<Switch disabled />}
                  label="Email Notifications"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={<Switch disabled />}
                  label="In-App Notifications"
                />
              </Grid>
            </Grid>
          </Box>
        );
      
      default:
        return null;
    }
  };
  
  return (
    <Card variant="outlined" sx={{ mb: 3 }}>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h5" component="h2">
            Edit Organization Configuration
          </Typography>
          <Button
            variant="outlined"
            color="primary"
            size="small"
            startIcon={<Refresh />}
            onClick={fetchCustomerOrganizations}
            disabled={loading}
          >
            Refresh
          </Button>
        </Box>
        
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}
        
        {loading ? (
          <Box display="flex" justifyContent="center" my={4}>
            <CircularProgress />
          </Box>
        ) : organizations.length === 0 ? (
          <Alert severity="info">
            No customer organizations found. Please create customer organizations first.
          </Alert>
        ) : (
          <>
            <Autocomplete
              options={organizations}
              getOptionLabel={(option) => option.name}
              value={selectedOrganization}
              onChange={handleOrganizationChange}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Organization"
                  variant="outlined"
                  fullWidth
                  margin="normal"
                />
              )}
              sx={{ mb: 3 }}
            />
            
            {selectedOrganization ? (
              configLoading ? (
                <Box display="flex" justifyContent="center" my={4}>
                  <CircularProgress />
                </Box>
              ) : (
                <>
                  <Paper variant="outlined" sx={{ mb: 3 }}>
                    <Tabs
                      value={activeTab}
                      onChange={handleTabChange}
                      variant="scrollable"
                      scrollButtons="auto"
                      sx={{
                        borderBottom: 1,
                        borderColor: 'divider',
                      }}
                    >
                      <Tab 
                        icon={<Settings fontSize="small" />} 
                        label="GENERAL" 
                        id="tab-0" 
                        aria-controls="tabpanel-0" 
                      />
                      <Tab 
                        icon={<Settings fontSize="small" />} 
                        label="SELF-ASSIGNMENT" 
                        id="tab-1" 
                        aria-controls="tabpanel-1" 
                      />
                      <Tab 
                        icon={<BrandingWatermark fontSize="small" />} 
                        label="BRANDING" 
                        id="tab-2" 
                        aria-controls="tabpanel-2" 
                      />
                      <Tab 
                        icon={<Notifications fontSize="small" />} 
                        label="NOTIFICATIONS" 
                        id="tab-3" 
                        aria-controls="tabpanel-3" 
                      />
                    </Tabs>
                    
                    <Box role="tabpanel" id={`tabpanel-${activeTab}`} aria-labelledby={`tab-${activeTab}`}>
                      {renderTabContent()}
                    </Box>
                  </Paper>
                  
                  <Box display="flex" justifyContent="flex-end">
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleSaveConfig}
                      disabled={saving || !config}
                      startIcon={<Save />}
                    >
                      {saving ? "Saving..." : "Save Configuration"}
                    </Button>
                  </Box>
                </>
              )
            ) : (
              <Alert severity="info" sx={{ mt: 2 }}>
                Please select an organization to configure its settings.
              </Alert>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default CustomerOrganizationConfig;
